import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCallback } from 'react';
import { nip19 } from 'nostr-tools';
import type { GenesisEvent } from '@/lib/torchTypes';
import { TORCH_GENESIS_KIND, APP_URL, OG_IMAGE_URL } from '@/lib/torchConstants';

interface CompanionNoteOptions {
  genesis: GenesisEvent;
  action: 'pass';
  recipientPubkey: string;   // hex pubkey of new holder
  mark?: string;
  passEventId?: string;
  zapAmountSats?: number;
}

/** Encode a hex pubkey as a nostr:npub1... mention */
function encodeMention(hexPubkey: string): string {
  try {
    return `nostr:${nip19.npubEncode(hexPubkey)}`;
  } catch {
    return hexPubkey.slice(0, 8) + '…';
  }
}

function torchUrl(genesis: GenesisEvent): string {
  return `${APP_URL}/torch/${genesis.slug}`;
}

export function buildCompanionNoteContent(options: CompanionNoteOptions): string {
  const { genesis, recipientPubkey, mark, zapAmountSats } = options;

  const title = genesis.title;
  const url = torchUrl(genesis);
  const recipientMention = encodeMention(recipientPubkey);
  const zapLine = zapAmountSats ? `⚡ ${zapAmountSats.toLocaleString()} sats` : '';

  return [
    `🔥 I just passed the torch "${title}" to ${recipientMention}${zapLine ? ` · ${zapLine}` : ''}.`,
    mark ? `"${mark}"` : null,
    mark ? `` : null,
    `${url}`,
    ``,
    `#torchlite`,
  ].filter(line => line !== null).join('\n');
}

export function buildCompanionNoteTags(options: CompanionNoteOptions): string[][] {
  const { genesis, recipientPubkey, passEventId } = options;

  const genesisRef = `${TORCH_GENESIS_KIND}:${genesis.pubkey}:${genesis.slug}`;

  const tags: string[][] = [
    ['a', genesisRef],
    ['t', 'torchlite'],
    // Tag the recipient so they get notified in every client
    ['p', recipientPubkey],
    // Attach the OG image for clients that render image tags
    ['url', OG_IMAGE_URL],
    ['imeta',
      `url ${OG_IMAGE_URL}`,
      'm image/jpeg',
      'dim 1536x1024',
      `alt Torchlite — ${genesis.title}`,
    ],
  ];

  if (passEventId) {
    tags.push(['q', passEventId]);
  }

  return tags;
}

export function usePublishCompanionNote() {
  const { mutate: publishEvent } = useNostrPublish();

  const publishCompanion = useCallback((options: CompanionNoteOptions) => {
    const content = buildCompanionNoteContent(options);
    const tags = buildCompanionNoteTags(options);

    publishEvent({
      kind: 1,
      content,
      tags,
    });
  }, [publishEvent]);

  return { publishCompanion };
}
